import React from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { cn } from "@/lib/utils";
import { CheckCircle2, Crown, ArrowRight } from "lucide-react";
import type { VitrineItem } from "./data/vitrineItems";

interface VitrineDemoModalProps {
  item: VitrineItem | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const VitrineDemoModal = ({ item, open, onOpenChange }: VitrineDemoModalProps) => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();

  if (!item) return null;
  const Icon = item.icon;

  const isFree = !user || user.plan === "free" || user.plan === "trial";
  const showPlansCTA = item.premium && isFree;

  const handleOpen = () => {
    onOpenChange(false);
    if (!isAuthenticated && item.premium) {
      navigate("/planos");
      return;
    }
    navigate(item.route);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl p-0 overflow-hidden animate-fade-in">
        {/* Hero do modal */}
        <div className={cn("relative aspect-video bg-gradient-to-br", item.gradient)}>
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
          <div className="absolute inset-0 flex items-center justify-center">
            <Icon className="h-24 w-24 text-white drop-shadow-2xl" strokeWidth={1.3} />
          </div>
        </div>

        <div className="p-6 space-y-4">
          <DialogHeader>
            <DialogTitle className="text-2xl font-extrabold flex items-center gap-2">
              {item.title}
              {item.premium && (
                <span className="inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-full bg-gradient-to-r from-amber-500 to-yellow-500 text-black">
                  <Crown className="h-3 w-3" /> PREMIUM
                </span>
              )}
            </DialogTitle>
            <DialogDescription className="text-base">{item.tagline}</DialogDescription>
          </DialogHeader>

          <div>
            <h4 className="font-semibold text-sm text-foreground mb-1">O que faz</h4>
            <p className="text-sm text-muted-foreground">{item.description}</p>
          </div>

          <div>
            <h4 className="font-semibold text-sm text-foreground mb-2">Como usar</h4>
            <ul className="space-y-1.5">
              {item.steps.map((step, i) => (
                <li key={i} className="flex gap-2 text-sm text-muted-foreground">
                  <CheckCircle2 className="h-4 w-4 text-emerald-500 mt-0.5 shrink-0" />
                  <span>{step}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="rounded-lg bg-muted p-3">
            <h4 className="font-semibold text-xs text-foreground mb-0.5">Ideal para</h4>
            <p className="text-sm text-muted-foreground">{item.idealPara}</p>
          </div>

          <div className="flex flex-col sm:flex-row gap-2 pt-2">
            <Button onClick={handleOpen} className="flex-1 bg-gradient-to-r from-yellow-500 to-amber-500 text-black hover:from-yellow-600 hover:to-amber-600 font-bold">
              Abrir ferramenta
              <ArrowRight className="ml-1 h-4 w-4" />
            </Button>
            {showPlansCTA && (
              <Button
                variant="outline"
                onClick={() => {
                  onOpenChange(false);
                  navigate("/planos");
                }}
                className="flex-1"
              >
                Ver planos
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default VitrineDemoModal;
